import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { locField } from "@/lib/i18nField";
import { formatDate } from "@/lib/format";
import { ShieldAlert, EyeOff, Trash2, Pin, Lock, Check } from "lucide-react";

export default function CommunityModeration() {
  const { lang, t } = useLang();
  const qc = useQueryClient();
  const { data: reports = [] } = useQuery({ queryKey: ["comReports"], queryFn: async () => (await api.get("/community/admin/reports")).data });
  const { data: stats } = useQuery({ queryKey: ["comModStats"], queryFn: async () => (await api.get("/community/admin/stats")).data });
  const { data: logs = [] } = useQuery({ queryKey: ["comLogs"], queryFn: async () => (await api.get("/community/admin/moderation/logs")).data });

  const act = async (report, action) => {
    try {
      await api.post("/community/admin/moderate", { action, content_type: report.target_type, content_id: report.target_id, report_id: report.id });
      toast.success(t("com_saved"));
      qc.invalidateQueries({ queryKey: ["comReports"] }); qc.invalidateQueries({ queryKey: ["comLogs"] });
    } catch { toast.error(t("com_error")); }
  };
  const resolve = async (report) => { try { await api.post("/community/admin/moderate", { action: "unhide", content_type: report.target_type, content_id: report.target_id, report_id: report.id }); qc.invalidateQueries({ queryKey: ["comReports"] }); } catch { toast.error(t("com_error")); } };

  return (
    <section className="mt-14" data-testid="community-moderation">
      <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-500" /> {t("com_admin_moderation")}</h2>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[["posts", stats.posts], ["comments", stats.comments], ["reactions", stats.reactions], ["open_reports", stats.open_reports]].map(([k, v]) => (
            <div key={k} className="bg-[#121418] border border-[#272A34] rounded-lg p-4" data-testid={`com-stat-${k}`}>
              <div className="font-serif text-3xl text-amber-400">{v}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mt-1">{k}</div>
            </div>
          ))}
        </div>
      )}
      {stats?.languages && (
        <div className="mb-8 text-xs font-mono text-stone-500 flex flex-wrap gap-3">{Object.entries(stats.languages).map(([l, n]) => <span key={l}>{l.toUpperCase()}: {n}</span>)}</div>
      )}

      <h3 className="font-serif text-lg text-stone-200 mb-3">{t("com_admin_reports")} ({reports.length})</h3>
      {reports.length === 0 ? (
        <div className="text-center py-10 text-stone-500 border border-dashed border-[#272A34] rounded-lg mb-8" data-testid="com-no-reports">{t("com_admin_no_reports")}</div>
      ) : (
        <div className="space-y-3 mb-10">
          {reports.map((rep) => (
            <div key={rep.id} data-testid={`com-report-${rep.id}`} className="bg-[#121418] border border-[#272A34] rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500">{rep.target_type} · {rep.motif}</span>
                <span className="font-mono text-[10px] text-stone-500">{formatDate(rep.created_at, lang)} · {rep.status}</span>
              </div>
              {rep.target && (
                <div className="text-sm text-stone-300 mb-2">
                  <span className="text-[10px] font-mono uppercase text-stone-600 mr-2">{t("com_admin_original")} [{rep.target.original_language}]</span>
                  <span className="line-clamp-2">{typeof rep.target.content === "string" ? rep.target.content : locField(rep.target.content, lang) || locField(rep.target.title, lang)}</span>
                  <span className="text-xs text-stone-500 block mt-1">— {rep.target.author_name}</span>
                </div>
              )}
              {rep.detail && <p className="text-xs text-stone-500 mb-2 italic">{rep.detail}</p>}
              <div className="flex flex-wrap gap-2">
                {rep.target_type === "post" && <Link to={`/community/post/${rep.target_id}`} className="text-xs text-amber-500 hover:text-amber-400 px-3 py-1.5">Open</Link>}
                <button data-testid={`mod-hide-${rep.id}`} onClick={() => act(rep, "hide")} className="inline-flex items-center gap-1 text-xs text-stone-300 border border-[#272A34] rounded-md px-3 py-1.5 hover:border-amber-500/40"><EyeOff className="w-3.5 h-3.5" /> {t("com_admin_hide")}</button>
                <button data-testid={`mod-delete-${rep.id}`} onClick={() => act(rep, "delete")} className="inline-flex items-center gap-1 text-xs text-red-300 border border-red-900/50 rounded-md px-3 py-1.5 hover:bg-red-950/40"><Trash2 className="w-3.5 h-3.5" /> {t("com_delete")}</button>
                {rep.target_type === "post" && <button data-testid={`mod-pin-${rep.id}`} onClick={() => act(rep, "pin")} className="inline-flex items-center gap-1 text-xs text-stone-300 border border-[#272A34] rounded-md px-3 py-1.5"><Pin className="w-3.5 h-3.5" /> {t("com_admin_pin")}</button>}
                {rep.target_type === "post" && <button data-testid={`mod-lock-${rep.id}`} onClick={() => act(rep, "lock")} className="inline-flex items-center gap-1 text-xs text-stone-300 border border-[#272A34] rounded-md px-3 py-1.5"><Lock className="w-3.5 h-3.5" /> {t("com_admin_lock")}</button>}
                <button data-testid={`mod-resolve-${rep.id}`} onClick={() => resolve(rep)} className="inline-flex items-center gap-1 text-xs text-emerald-300 border border-emerald-900/50 rounded-md px-3 py-1.5"><Check className="w-3.5 h-3.5" /> {t("com_admin_resolve")}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 className="font-serif text-lg text-stone-200 mb-3">{t("com_admin_logs")}</h3>
      <div className="space-y-1.5">
        {logs.slice(0, 30).map((l) => (
          <div key={l.id} className="text-xs font-mono text-stone-500 flex flex-wrap gap-2 bg-[#121418] border border-[#272A34] rounded-md px-3 py-2">
            <span className="text-amber-500">{l.action}</span><span>{l.content_type}</span><span className="text-stone-600">{l.moderator_name}</span><span className="text-stone-600">{formatDate(l.created_at, lang)}</span>{l.reason && <span className="italic">{l.reason}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
